import React, { useEffect, useRef } from 'react';
import tt from '@tomtom-international/web-sdk-maps';
import '@tomtom-international/web-sdk-maps/dist/maps.css';
import polylineUtil from '@mapbox/polyline';
import { Station } from '../types';
import './RouteMap.css';
export const EMPTY_STATIONS: Station[] = [];


interface RouteMapProps {
  encodedPolyline: string;
  startLabel?: string;
  endLabel?: string;
  stations?: Station[];
}

const API_KEY = process.env.REACT_APP_TOMTOM_API_KEY;

type AvailabilityState = 'high' | 'medium' | 'low' | 'unknown';

function getAvailabilityState(available: number, total: number): AvailabilityState {
  if (total <= 0) return 'unknown';
  const ratio = available / total;
  if (ratio >= 0.6) return 'high';
  if (ratio >= 0.25) return 'medium';
  return 'low';
}

function escapeHtml(value?: string | null): string {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function createStationMarkerHtml(station: Station): string {
  const availabilityState = getAvailabilityState(station.AvailablePorts, station.TotalPorts);
  const stationName = escapeHtml(station.LocName || 'Charging Station');

  return `
    <button type="button" class="station-marker station-marker--${availabilityState}" aria-label="${stationName}: ${station.AvailablePorts} of ${station.TotalPorts} ports available">
    <span class="station-marker__dot" aria-hidden="true"></span>
    <span class="station-marker__badge">${station.AvailablePorts}/${station.TotalPorts}</span>
    </button>
  `;
}

function createRoutePointMarkerHtml(letter: string, variant: 'origin' | 'destination'): string {
  return `
    <div class="route-point-marker route-point-marker--${variant}" aria-label="${variant === 'origin' ? 'Origin' : 'Destination'}">
      <span class="route-point-marker__letter">${escapeHtml(letter)}</span>
    </div>
  `;
}

function createRoutePointPopupHtml(label: string, subtitle: string): string {
  return `
    <div class="route-popup">
      <h4 class="route-popup__title">${escapeHtml(label)}</h4>
      <p class="route-popup__subtitle">${escapeHtml(subtitle)}</p>
    </div>
  `;
}

function createStationPopupHtml(station: Station): string {
  const availabilityState = getAvailabilityState(station.AvailablePorts, station.TotalPorts);
  const availabilityLabel =
    availabilityState === 'high'
      ? 'High Availability'
      : availabilityState === 'medium'
        ? 'Moderate Availability'
        : availabilityState === 'low'
          ? 'Low Availability'
          : 'Availability Unknown';

  const name = escapeHtml(station.LocName || 'Charging Station');
  const address = escapeHtml(station.Address || 'Address unavailable');
  const network = station.NetworkName ? `<p class="station-popup__network">Network: ${escapeHtml(station.NetworkName)}</p>` : '';

  return `
    <div class="station-popup">
      <h4 class="station-popup__title">${name}</h4>
      <p class="station-popup__address">${address}</p>
      <div class="station-popup__meta">
        <span class="station-popup__chip">${station.AvailablePorts}/${station.TotalPorts} ports</span>
        <span class="station-popup__chip station-popup__chip--${availabilityState}">${availabilityLabel}</span>
      </div>
      ${network}
    </div>
  `;
}

export default function RouteMap({ encodedPolyline, startLabel = 'Start', endLabel = 'Destination', stations = EMPTY_STATIONS }: RouteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    // Cleanup previous map if it exists
    if (mapRef.current) {
      console.log('[RouteMap] Cleaning up previous map');
      mapRef.current.remove();
      mapRef.current = null;
    }

    if (!mapContainer.current) return;
    if (!API_KEY) {
      console.error('TomTom API key not found. Please set REACT_APP_TOMTOM_API_KEY in your .env file');
      return;
    }

    const validStations = stations.filter(
      (station) => typeof station.Lat === 'number' && typeof station.Long === 'number'
    );
    const hasRoute = Boolean(encodedPolyline?.trim());

    if (!hasRoute && validStations.length === 0) return;

    try {
      let coordinates: [number, number][] = [];

      if (hasRoute) {
        console.log('[RouteMap] Creating new map for polyline:', encodedPolyline.substring(0, 50) + '...');
        // Decode the polyline
        const decoded = polylineUtil.decode(encodedPolyline);
        coordinates = decoded.map(([lat, lng]) => [lng, lat]); // TomTom expects [lng, lat]
      } else {
        console.log('[RouteMap] Creating new map for station-only results');
      }

      if (hasRoute && coordinates.length === 0) {
        console.error('No coordinates decoded from polyline');
      }

      if (coordinates.length > 0) {
        console.log('[RouteMap] Decoded', coordinates.length, 'coordinates');
      }

      const initialCenter: [number, number] =
        coordinates[0] ?? [validStations[0].Long, validStations[0].Lat];

      // Initialize the map
      const map = tt.map({
        key: API_KEY,
        container: mapContainer.current,
        center: initialCenter,
        zoom: 13,
      });
      mapRef.current = map;

      map.on('load', () => {
        if (coordinates.length > 0) {
          // Add route line source
          map.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates,
              },
            },
          });

          // Add route line layer
          map.addLayer({
            id: 'route-line',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#7B3FC4',
              'line-width': 5,
              'line-opacity': 0.8,
            },
          });
        }

        // Fit map to route bounds, including station points
        const allPoints: [number, number][] = [
          ...coordinates,
          ...validStations.map((station) => [station.Long, station.Lat] as [number, number]),
        ];
        const bounds = allPoints.reduce(
          (b, coord) => b.extend(coord),
          new tt.LngLatBounds(allPoints[0], allPoints[0])
        );
        map.fitBounds(bounds, { padding: 50 });

        if (coordinates.length > 0) {
          // Add start marker with popup
          const startPopup = new tt.Popup({ offset: 40, closeButton: false }).setHTML(
            createRoutePointPopupHtml(startLabel, 'Route origin')
          );
          const startEl = document.createElement('div');
          startEl.innerHTML = createRoutePointMarkerHtml('A', 'origin');
          new tt.Marker({ element: startEl.firstElementChild as HTMLElement, anchor: 'bottom' } as any)
            .setLngLat(coordinates[0])
            .setPopup(startPopup)
            .addTo(map);

          // Add end marker with popup
          const endPopup = new tt.Popup({ offset: 40, closeButton: false }).setHTML(
            createRoutePointPopupHtml(endLabel, 'Route destination')
          );
          const endEl = document.createElement('div');
          endEl.innerHTML = createRoutePointMarkerHtml('B', 'destination');
          new tt.Marker({ element: endEl.firstElementChild as HTMLElement, anchor: 'bottom' } as any)
            .setLngLat(coordinates[coordinates.length - 1])
            .setPopup(endPopup)
            .addTo(map);
        }

        // Add station markers along the route
        validStations.forEach((station) => {
          const availabilityState = getAvailabilityState(station.AvailablePorts, station.TotalPorts);
          const stationPopup = new tt.Popup({ offset: 24 }).setHTML(createStationPopupHtml(station));

          const stationMarker = new tt.Marker({ color: '#0f172a' })
            .setLngLat([station.Long, station.Lat])
            .setPopup(stationPopup)
            .addTo(map);

          const markerApi = stationMarker as unknown as { getElement?: () => HTMLElement };
          const markerEl = markerApi.getElement ? markerApi.getElement() : null;
          if (markerEl) {
            markerEl.classList.add('station-marker-shell', `station-marker-shell--${availabilityState}`);
            markerEl.innerHTML = createStationMarkerHtml(station);
          }
        });
      });

      // Cleanup on unmount
      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }, [encodedPolyline, startLabel, endLabel, stations]);

  if (!API_KEY) {
    return (
      <div className="route-map-error">
        <p>⚠️ TomTom API key not configured</p>
        <p className="error-hint">Add REACT_APP_TOMTOM_API_KEY to your .env file</p>
      </div>
    );
  }

  return (
    <div className="route-map-wrapper">
      <div ref={mapContainer} className="route-map-container" />
    </div>
  );
}
